import React, { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { collection, doc, getDocs, updateDoc } from "firebase/firestore";
import { UserData } from "../../interface/user.interface";
import { db } from "../../lib/firebase";
import UserTable from "../../components/userTable";

export default function AllUsers() {
  const [filter, setFilter] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [users, setUsers] = useState<UserData[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const usersCollection = collection(db, "users");
        const usersnapshot = await getDocs(usersCollection);
        const userList = usersnapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as Omit<UserData, "id">),
        }));
        setUsers(userList);
      } catch (error) {
        console.error("Error fetching users: ", error);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const updateUserRole = async (id: string, newRole: string) => {
    try {
      const userDocRef = doc(db, "users", id);
      await updateDoc(userDocRef, { role: newRole });
      setUsers((prevUsers: any) =>
        prevUsers.map((user: any) =>
          user.id === id ? { ...user, role: newRole } : user
        )
      );
    } catch (error) {
      console.error("Error updating user role:", error);
    }
  };

  const updateUserStatus = async (id: string, newStatus: boolean) => {
    try {
      const userDocRef = doc(db, "users", id);
      await updateDoc(userDocRef, { active: newStatus });
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === id ? { ...user, active: newStatus } : user
        )
      );
    } catch (error) {
      console.error("Error updating user status:", error);
    }
  };

  const filteredUsers = users.filter((usr: UserData) => {
    const usrName = usr.name || "";
    const matchesSearch = usrName
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesFilter =
      filter === "all" ||
      (filter === "active" && usr.active) ||
      (filter === "inactive" && !usr.active);
    return matchesSearch && matchesFilter;
  });

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-center mb-8 space-y-4 sm:space-y-0">
        <div className="flex items-center w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name"
              // className="pl-10 pr-4 py-2 w-full sm:w-64 bg-background border-gray-700 rounded-full focus:ring-2 focus:ring-primary transition-all"
              className="w-full pl-9 pr-2 focus-visible:outline-none h-[50px] bg-bgCard border rounded-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        {/* Dropdown */}
        <div className="flex items-center space-x-4">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-bgCard border-bg rounded-full py-2 px-2 focus:ring-2 focus:ring-primary"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      {/* Users List */}
      <UserTable
        data={filteredUsers}
        isLoading={loading}
        onUpdateRole={updateUserRole}
        onUpdateStatus={updateUserStatus}
      />
    </div>
  );
}
